import type { ChauffeurPage } from "../slug-chauffeur";

import heroImage from "@/assets/images/chauffeur/events/event-soir.png";

const niceJazzFestivalEn: ChauffeurPage = {
  slug: "nice-jazz-festival",
  title: "Private chauffeur Nice Jazz Festival – VTC & transfers",
  subtitle:
    "Private chauffeur during the Nice Jazz Festival: access to Place Masséna and the Albert 1er gardens, airport transfers and late-night returns after the concerts.",
  keywords: [
    "nice jazz festival chauffeur",
    "nice jazz festival private driver",
    "concert chauffeur nice",
  ],
  hero: {
    title: "Private chauffeur during the Nice Jazz Festival",
    subtitle:
      "Concerts at Place Masséna and the Théâtre de Verdure: a dedicated chauffeur when central Nice closes and parking disappears.",
    keywords: [
      "Nice Jazz Festival chauffeur",
      "Place Masséna VTC",
      "Nice airport concert transfer",
    ],
    imageSrc: heroImage.src,
    imageAlt: "Private chauffeur during the Nice Jazz Festival",
    badges: ["Airport welcome", "City-centre drop-off", "Late-night returns"],
  },
  benefits: {
    heading: "Why a chauffeur during the Nice Jazz Festival",
    items: [
      {
        title: "Central Nice with no parking",
        description:
          "Place Masséna and the Albert 1er gardens sit in the heart of the city: the chauffeur drops you as close as possible and leaves, with no circling for a space.",
      },
      {
        title: "Airport minutes away",
        description:
          "Nice Côte d'Azur is less than 7 km from the festival. Name-board welcome, flight tracking and a direct transfer to your Nice hotel.",
      },
      {
        title: "Evening concerts",
        description:
          "Stages play late into July: the chauffeur waits and secures your return the moment the concert ends, with no taxi queue.",
      },
      {
        title: "5.0/5 from 38 Google reviews",
        description:
          "Punctuality, discretion and immaculate vehicles: the consistency that earns KS Rent Services a 5.0/5 rating from 38 Google reviews.",
      },
    ],
  },
  services: {
    heading: "Chauffeur services during the Nice Jazz Festival",
    services: [
      {
        title: "Nice airport meet-and-greet",
        text: "Airport ↔ central Nice transfer with flight tracking, minutes from the Albert 1er gardens.",
      },
      {
        title: "Drop-off nearest the stages",
        text: "Approach to Place Masséna and the Théâtre de Verdure within the closed streets and concert timing.",
      },
      {
        title: "Evening hire",
        text: "A dedicated chauffeur for dinner and the concert, waiting on site.",
      },
      {
        title: "Late-night returns",
        text: "A late finish: a direct return to your hotel in Nice, Cannes or along the Riviera.",
      },
      {
        title: "Group & guest shuttles",
        text: "Multi-vehicle coordination for families, groups of friends and guests.",
      },
    ],
  },
  locations: {
    heading: "Key points during the Nice Jazz Festival",
    items: [
      {
        name: "Place Masséna",
        description: "Main stage in the heart of Nice, served by the tramway.",
      },
      {
        name: "Albert 1er gardens / Théâtre de Verdure",
        description: "Open-air stage between the Promenade and the centre.",
      },
      {
        name: "Nice Côte d'Azur airport",
        description: "Terminals 1 and 2, less than 7 km from the festival.",
      },
      {
        name: "Promenade des Anglais & hotels",
        description: "Seafront palaces and hotels, minutes away.",
      },
      {
        name: "Old Nice",
        description:
          "Restaurants and bars for dinner before or after the concert.",
      },
    ],
  },
  generic: {
    heading: "The Nice Jazz Festival from a chauffeur's seat",
    subheading:
      "Every summer in July, central Nice comes alive for several nights between Place Masséna and the Albert 1er gardens.",
    paragraphs: [
      "Every July, the Nice Jazz Festival sets its stages in the heart of the city, at Place Masséna and the Théâtre de Verdure in the Albert 1er gardens. The surrounding streets are closed to traffic and parking becomes impossible: a chauffeur drops you at the nearest authorised point, without wasting time hunting for a space.",
      "Nice Côte d'Azur airport is less than seven kilometres from the festival. With flight tracking and a name-board welcome, your transfer to the hotel runs smoothly, even at the height of the summer season.",
      "Concerts finish late and taxis grow scarce on the way out. Hourly hire keeps the same chauffeur for dinner and the concert, and guarantees a direct return from the very last note.",
    ],
    bullets: [
      "Drop-off nearest the stages",
      "Airport under 7 km",
      "Returns after the concerts",
    ],
  },
  process: {
    heading: "Booking a chauffeur for the Nice Jazz Festival",
    steps: [
      "Tell us your concert nights, your hotel and your timings.",
      "We plan the drop-offs around the streets closed near Place Masséna.",
      "The chauffeur arrives in uniform, with flight tracking for your Nice arrivals.",
      "Direct transfer or hourly hire to cover dinner, concert and return.",
    ],
  },
  faq: [
    {
      q: "How do I book a private chauffeur during the Nice Jazz Festival?",
      a: "Book ahead: in July, Nice is fully booked and demand rises on concert nights. Share your dates, your hotel and your timings so we can secure a dedicated chauffeur.",
    },
    {
      q: "Where can the chauffeur drop me near the stages?",
      a: "The festival is held at Place Masséna and the Albert 1er gardens, in a pedestrian, traffic-restricted zone. The chauffeur drops you at the nearest authorised point, then collects you at the same spot when the concert ends.",
    },
    {
      q: "Do you handle transfers from Nice airport?",
      a: "Yes, with flight tracking and a name-board welcome. Nice Côte d'Azur airport is less than 7 km from the centre: the transfer to your hotel is quick, even in high season.",
    },
    {
      q: "Is parking really a problem during the festival?",
      a: "Yes. In central Nice, car parks fill up and the streets around the stages are closed on concert nights. The chauffeur saves you the search for a space: he drops off, waits or returns at the agreed time.",
    },
    {
      q: "Do you cover late returns after the concerts?",
      a: "Yes. Concerts finish late and taxis are scarce on the way out: the chauffeur waits and guarantees your direct return to the hotel, in Nice or elsewhere on the Riviera.",
    },
    {
      q: "What vehicles do you offer?",
      a: "Saloons, SUVs and vans with a chauffeur depending on your group size, for a comfortable ride between the hotel, dinner and the festival.",
    },
    {
      q: "Can you take care of a family or a group?",
      a: "Yes. We coordinate several vehicles for families and groups of friends, with the same level of punctuality for everyone.",
    },
  ],
  relatedLinks: [
    {
      label: "Private chauffeur in Nice",
      href: "/en/chauffeur/nice",
    },
    {
      label: "Meet and greet at Nice Airport",
      href: "/en/chauffeur/airport",
    },
    {
      label: "French Riviera rates and routes",
      href: "/en/chauffeur/french-riviera",
    },
    {
      label: "Rent a car Nice",
      href: "/en/location/car-rental-nice",
    },
  ],
  cta: {
    title: "Your chauffeur for the Nice Jazz Festival",
    text: "Airport welcome, drop-off nearest the stages and late-night returns: book a dedicated chauffeur for your concert evenings.",
    primaryHref: "/en/reservation",
    primaryLabel: "Book a chauffeur",
  },
  seo: {
    title: "Nice Jazz Festival Chauffeur – Private car service | KS Rent",
    description:
      "Private chauffeur for the Nice Jazz Festival: airport transfer, drop-off at Place Masséna and the Albert 1er gardens, late-night returns after the concerts.",
    canonical: "/en/chauffeur/nice-jazz-festival",
  },
};

export default niceJazzFestivalEn;
